/**
 * Marina — Restock Reconciliation
 * Shrinkage Layer 4 (the single biggest dollar-saving control).
 *
 * Flow:
 *   1. Staff enters PIN → starts a restock session (trays go RESTOCK-blue in firmware)
 *   2. Staff restocks physically, enters new weights per slot (or firmware auto-reports)
 *   3. On Complete, backend reconciles vs par-levels → shrinkage events
 *   4. View shrinkage log with $ lost + resolve workflow
 */
import { useEffect, useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Package, ShieldAlert, CheckCircle2, Lock, Play, Save, RefreshCw,
  DollarSign, AlertTriangle, ArrowLeft, Pencil, Plus, Trash2, SlidersHorizontal,
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';
import TerminalFloorNav from '@/components/TerminalFloorNav';

const HOTEL_ID_DEFAULT = 'hotel_grand_marina';
const ROOM_ID_DEFAULT  = 'room_101';

const DEFAULT_PAR_SEED = [
  { tray_id: 'tray_a1b2c3', slot_id: 0, sku: 'snickers_50g',  expected_weight_g: 50,  expected_units: 6, price_usd: 4.50 },
  { tray_id: 'tray_a1b2c3', slot_id: 1, sku: 'pringles_mini', expected_weight_g: 40,  expected_units: 6, price_usd: 5.00 },
  { tray_id: 'tray_a1b2c3', slot_id: 2, sku: 'coke_can_330',  expected_weight_g: 360, expected_units: 4, price_usd: 3.50 },
  { tray_id: 'tray_a1b2c3', slot_id: 3, sku: 'mini_prosecco', expected_weight_g: 320, expected_units: 2, price_usd: 12.00 },
  { tray_id: 'tray_a1b2c3', slot_id: 4, sku: 'toblerone_100', expected_weight_g: 100, expected_units: 3, price_usd: 7.50 },
  { tray_id: 'tray_a1b2c3', slot_id: 5, sku: 'almonds_80g',   expected_weight_g: 80,  expected_units: 3, price_usd: 6.00 },
];

export default function RestockReconciliationPage() {
  const [hotelId] = useState(HOTEL_ID_DEFAULT);
  const [roomId, setRoomId] = useState(ROOM_ID_DEFAULT);
  const [pin, setPin] = useState('');
  const [session, setSession] = useState(null);         // active session or null
  const [pars, setPars] = useState([]);
  const [weights, setWeights] = useState({});           // { "tray_id:slot_id": grams }
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [tab, setTab] = useState('restock');            // 'restock' | 'par'
  const [parDraft, setParDraft] = useState([]);         // editable copy

  /* ─── Load par-levels + recent shrinkage events ────────────────── */
  const loadPars = useCallback(async () => {
    try {
      const r = await fetch(
        `${BACKEND_URL}/api/iot/par-levels?hotel_id=${hotelId}&room_id=${roomId}`,
      );
      const d = await r.json();
      if (d.par_levels?.length) {
        setPars(d.par_levels);
      } else {
        // Seed defaults for demo
        setPars(DEFAULT_PAR_SEED.map((p) => ({ hotel_id: hotelId, room_id: roomId, ...p })));
      }
    } catch (e) {
      setPars(DEFAULT_PAR_SEED.map((p) => ({ hotel_id: hotelId, room_id: roomId, ...p })));
    }
  }, [hotelId, roomId]);

  const loadEvents = useCallback(async () => {
    setLoadingEvents(true);
    try {
      const r = await fetch(
        `${BACKEND_URL}/api/iot/shrinkage/events?hotel_id=${hotelId}&limit=20`,
      );
      const d = await r.json();
      setEvents(d.events || []);
    } catch (e) {
      setEvents([]);
    } finally {
      setLoadingEvents(false);
    }
  }, [hotelId]);

  useEffect(() => { loadPars(); loadEvents(); }, [loadPars, loadEvents]);
  useEffect(() => { setParDraft(pars.map((p) => ({ ...p }))); }, [pars]);

  /* ─── Par-level editor handlers ───────────────────────────────── */
  const updateParField = (idx, field, value) => {
    const next = [...parDraft];
    const parsed = ['expected_weight_g', 'expected_units', 'price_usd', 'slot_id'].includes(field)
      ? (value === '' ? '' : Number(value))
      : value;
    next[idx] = { ...next[idx], [field]: parsed };
    setParDraft(next);
  };

  const addParRow = () => {
    setParDraft([
      ...parDraft,
      {
        hotel_id: hotelId,
        room_id: roomId,
        tray_id: 'tray_a1b2c3',
        slot_id: parDraft.length,
        sku: '',
        expected_weight_g: 0,
        expected_units: 1,
        price_usd: 0,
      },
    ]);
  };

  const removeParRow = (idx) => {
    setParDraft(parDraft.filter((_, i) => i !== idx));
  };

  const saveParLevels = async () => {
    // Basic validation
    const invalid = parDraft.find(
      (p) => !p.sku || !p.tray_id || p.expected_weight_g <= 0 || p.expected_units <= 0,
    );
    if (invalid) { toast.error('Every row needs SKU, tray_id, positive weight and units'); return; }
    try {
      const r = await fetch(`${BACKEND_URL}/api/iot/par-levels`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          par_levels: parDraft.map((p) => ({
            ...p,
            hotel_id: hotelId,
            room_id: roomId,
          })),
        }),
      });
      const d = await r.json();
      toast.success(`Saved · ${d.upserted} entr${d.upserted === 1 ? 'y' : 'ies'}`);
      loadPars();
    } catch (e) { toast.error('Save failed'); }
  };

  /* ─── Seed par-levels (one-click demo helper) ──────────────────── */
  const seedDefaults = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/iot/par-levels`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          par_levels: DEFAULT_PAR_SEED.map((p) => ({
            hotel_id: hotelId, room_id: roomId, ...p,
          })),
        }),
      });
      const d = await r.json();
      toast.success(`Seeded ${d.upserted} par-level entries`);
      loadPars();
    } catch (e) {
      toast.error('Seed failed');
    }
  };

  /* ─── Session lifecycle ────────────────────────────────────────── */
  const startRestock = async () => {
    if (!pin.trim()) { toast.error('Enter staff PIN'); return; }
    const trayIds = [...new Set(pars.map((p) => p.tray_id))];
    if (trayIds.length === 0) { toast.error('No par-levels configured. Seed first.'); return; }
    try {
      const r = await fetch(`${BACKEND_URL}/api/iot/restock/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hotel_id: hotelId, room_id: roomId, tray_ids: trayIds, staff_pin: pin,
        }),
      });
      if (r.status === 401) { toast.error('Invalid staff PIN'); return; }
      const d = await r.json();
      setSession(d.session);
      // Pre-fill weights with par-level expectations
      const w = {};
      pars.forEach((p) => {
        w[`${p.tray_id}:${p.slot_id}`] = p.expected_weight_g * p.expected_units;
      });
      setWeights(w);
      toast.success('Restock mode active · Trays paused');
    } catch (e) {
      toast.error('Could not start restock: ' + e.message);
    }
  };

  const completeRestock = async () => {
    if (!session) return;
    // Group weights by tray
    const new_readings = {};
    Object.entries(weights).forEach(([k, v]) => {
      const [t, s] = k.split(':');
      if (!new_readings[t]) new_readings[t] = {};
      new_readings[t][parseInt(s, 10)] = parseFloat(v) || 0;
    });
    try {
      const r = await fetch(`${BACKEND_URL}/api/iot/restock/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: session.id, staff_pin: pin, new_readings,
        }),
      });
      if (r.status === 401) { toast.error('Invalid staff PIN'); return; }
      const d = await r.json();
      if (d.shrinkage_count > 0) {
        toast.error(
          `Restock complete · ${d.shrinkage_count} shrinkage event${d.shrinkage_count > 1 ? 's' : ''} · $${d.total_loss_usd} lost`,
          { duration: 6000 },
        );
      } else {
        toast.success('Restock complete · zero shrinkage', { duration: 4000 });
      }
      setSession(null);
      setWeights({});
      loadEvents();
    } catch (e) {
      toast.error('Completion failed: ' + e.message);
    }
  };

  const resolveEvent = async (eventId) => {
    try {
      await fetch(
        `${BACKEND_URL}/api/iot/shrinkage/${eventId}/resolve?resolved_by=marina-ui`,
        { method: 'POST' },
      );
      toast.success('Event resolved');
      loadEvents();
    } catch (e) { toast.error('Resolve failed'); }
  };

  /* ─── Derived totals ───────────────────────────────────────────── */
  const unresolvedLoss = useMemo(
    () => events.filter((e) => !e.resolved && e.delta_units < 0)
                .reduce((s, e) => s + Math.abs(e.delta_value_usd || 0), 0),
    [events],
  );
  const unresolvedCount = useMemo(
    () => events.filter((e) => !e.resolved).length,
    [events],
  );

  return (
    <div className="terminal-theme min-h-screen flex flex-col" data-testid="restock-reconciliation-page">
      {/* ── Header ── */}
      <header className="border-b border-[color:var(--t-border)] px-5 sm:px-8 py-4 flex items-center gap-4">
        <Link to="/marina" className="t-btn-ghost h-8 px-3 text-xs inline-flex items-center gap-1.5">
          <ArrowLeft className="w-3 h-3" /> MARINA
        </Link>
        <div className="flex-1">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[color:var(--t-text-3)]">
            shrinkage control · layer 4
          </div>
          <h1 className="font-mono text-xl text-[color:var(--t-text)] font-semibold tracking-tight">
            Restock Reconciliation
          </h1>
        </div>
        <div className="text-right">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[color:var(--t-text-3)]">open loss (unresolved)</div>
          <div className="font-mono text-2xl font-bold text-[color:var(--t-amber)] tabular-nums" data-testid="restock-unresolved-loss">
            ${unresolvedLoss.toFixed(2)}
          </div>
        </div>
      </header>

      {/* ── Tabs ── */}
      <div className="px-5 sm:px-8 pt-4 border-b border-[color:var(--t-border)] flex gap-1" data-testid="restock-tabs">
        {[
          { id: 'restock', label: 'RESTOCK SESSION', icon: Package },
          { id: 'par', label: 'PAR LEVELS', icon: SlidersHorizontal },
        ].map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`restock-tab-${t.id}`}
              className={`h-9 px-4 font-mono text-[11px] uppercase tracking-[0.18em] inline-flex items-center gap-1.5 border-b-2 transition-colors -mb-px ${active
                ? 'border-[color:var(--t-amber)] text-[color:var(--t-amber)]'
                : 'border-transparent text-[color:var(--t-text-3)] hover:text-[color:var(--t-text-2)]'
              }`}
            >
              <Icon className="w-3 h-3" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* ── Body ── */}
      <main className="flex-1 px-5 sm:px-8 py-6 grid lg:grid-cols-12 gap-6">

        {tab === 'par' && (
          <section className="lg:col-span-12 space-y-4" data-testid="par-editor-panel">
            <div className="border border-[color:var(--t-border)] rounded-lg bg-[color:var(--t-bg-2)] p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="font-mono text-xs uppercase tracking-[0.18em] text-[color:var(--t-text-3)] flex items-center gap-2">
                  <SlidersHorizontal className="w-3 h-3" /> par levels · {roomId}
                </div>
                <div className="flex gap-2">
                  <button onClick={addParRow} data-testid="par-add-row" className="t-btn-ghost h-8 px-3 text-[11px] font-mono inline-flex items-center gap-1.5">
                    <Plus className="w-3 h-3" /> ADD ROW
                  </button>
                  <button onClick={saveParLevels} data-testid="par-save-btn" className="t-btn-primary h-8 px-4 text-[11px] font-mono inline-flex items-center gap-1.5">
                    <Save className="w-3 h-3" /> SAVE ALL
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2 mb-2 font-mono text-[10px] uppercase tracking-[0.15em] text-[color:var(--t-text-3)]">
                <div className="col-span-3">tray · slot</div>
                <div className="col-span-3">sku</div>
                <div className="col-span-2 text-right">wt/unit (g)</div>
                <div className="col-span-2 text-right">units</div>
                <div className="col-span-1 text-right">price $</div>
                <div className="col-span-1"></div>
              </div>
              <div className="space-y-2">
                {parDraft.length === 0 && (
                  <div className="border border-dashed border-[color:var(--t-border)] rounded p-6 text-center font-mono text-xs text-[color:var(--t-text-3)]" data-testid="par-empty">
                    No par levels yet · tap ADD ROW or SEED DEMO DATA
                  </div>
                )}
                {parDraft.map((p, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`par-row-${idx}`}>
                    <div className="col-span-3 grid grid-cols-2 gap-1">
                      <input type="text" value={p.tray_id} onChange={(e) => updateParField(idx, 'tray_id', e.target.value)}
                        data-testid={`par-tray-${idx}`}
                        className="bg-[color:var(--t-bg)] border border-[color:var(--t-border)] rounded px-2 py-1.5 text-xs font-mono text-[color:var(--t-text)]" />
                      <input type="number" value={p.slot_id} onChange={(e) => updateParField(idx, 'slot_id', e.target.value)}
                        data-testid={`par-slot-${idx}`} min="0" max="5"
                        className="bg-[color:var(--t-bg)] border border-[color:var(--t-border)] rounded px-2 py-1.5 text-xs font-mono text-[color:var(--t-text)] text-right" />
                    </div>
                    <input type="text" value={p.sku} placeholder="e.g. snickers_50g" onChange={(e) => updateParField(idx, 'sku', e.target.value)}
                      data-testid={`par-sku-${idx}`}
                      className="col-span-3 bg-[color:var(--t-bg)] border border-[color:var(--t-border)] rounded px-2 py-1.5 text-xs font-mono text-[color:var(--t-text)]" />
                    <input type="number" value={p.expected_weight_g} onChange={(e) => updateParField(idx, 'expected_weight_g', e.target.value)}
                      data-testid={`par-weight-${idx}`}
                      className="col-span-2 bg-[color:var(--t-bg)] border border-[color:var(--t-border)] rounded px-2 py-1.5 text-xs font-mono text-[color:var(--t-text)] text-right" />
                    <input type="number" value={p.expected_units} onChange={(e) => updateParField(idx, 'expected_units', e.target.value)}
                      data-testid={`par-units-${idx}`}
                      className="col-span-2 bg-[color:var(--t-bg)] border border-[color:var(--t-border)] rounded px-2 py-1.5 text-xs font-mono text-[color:var(--t-text)] text-right" />
                    <input type="number" step="0.01" value={p.price_usd} onChange={(e) => updateParField(idx, 'price_usd', e.target.value)}
                      data-testid={`par-price-${idx}`}
                      className="col-span-1 bg-[color:var(--t-bg)] border border-[color:var(--t-border)] rounded px-2 py-1.5 text-xs font-mono text-[color:var(--t-text)] text-right" />
                    <button onClick={() => removeParRow(idx)} data-testid={`par-remove-${idx}`}
                      className="col-span-1 h-8 rounded border border-[color:var(--t-border)] text-[color:var(--t-text-3)] hover:text-red-400 hover:border-red-400 inline-flex items-center justify-center">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
              {pars.length === 0 && parDraft.length === 0 && (
                <div className="mt-4 pt-3 border-t border-[color:var(--t-border)]">
                  <button onClick={seedDefaults} data-testid="par-seed-btn" className="t-btn-ghost h-8 px-4 text-[11px] font-mono">
                    SEED DEMO DATA (6 items)
                  </button>
                </div>
              )}
            </div>
          </section>
        )}

        {tab === 'restock' && (
        <>
        {/* Session panel */}
        <section className="lg:col-span-7 space-y-5" data-testid="restock-session-panel">
          <div className="border border-[color:var(--t-border)] rounded-lg bg-[color:var(--t-bg-2)] p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="font-mono text-xs uppercase tracking-[0.18em] text-[color:var(--t-text-3)] flex items-center gap-2">
                <Package className="w-3 h-3" /> active session
              </div>
              {session ? (
                <span className="font-mono text-[10px] px-2 py-0.5 rounded-full bg-[color:var(--t-amber)]/20 text-[color:var(--t-amber)] tracking-wide" data-testid="restock-status-active">
                  IN PROGRESS
                </span>
              ) : (
                <span className="font-mono text-[10px] px-2 py-0.5 rounded-full bg-[color:var(--t-text-3)]/15 text-[color:var(--t-text-3)] tracking-wide" data-testid="restock-status-idle">
                  IDLE
                </span>
              )}
            </div>

            {!session && (
              <div className="space-y-4">
                <div className="flex flex-wrap gap-3 items-end">
                  <div className="flex-1 min-w-[150px]">
                    <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-[color:var(--t-text-3)] block mb-1">
                      room
                    </label>
                    <input
                      type="text"
                      value={roomId}
                      onChange={(e) => setRoomId(e.target.value)}
                      data-testid="restock-room-input"
                      className="w-full bg-[color:var(--t-bg)] border border-[color:var(--t-border)] rounded px-3 py-2 text-sm font-mono text-[color:var(--t-text)] focus:border-[color:var(--t-amber)] outline-none"
                    />
                  </div>
                  <div className="flex-1 min-w-[150px]">
                    <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-[color:var(--t-text-3)] block mb-1 flex items-center gap-1">
                      <Lock className="w-2.5 h-2.5" /> staff pin
                    </label>
                    <input
                      type="password"
                      value={pin}
                      onChange={(e) => setPin(e.target.value)}
                      placeholder="****"
                      data-testid="restock-pin-input"
                      className="w-full bg-[color:var(--t-bg)] border border-[color:var(--t-border)] rounded px-3 py-2 text-sm font-mono text-[color:var(--t-text)] focus:border-[color:var(--t-amber)] outline-none tracking-widest"
                    />
                  </div>
                  <button
                    onClick={startRestock}
                    disabled={!pin.trim()}
                    data-testid="restock-start-btn"
                    className="t-btn-primary h-10 px-5 text-xs font-mono inline-flex items-center gap-1.5 disabled:opacity-40"
                  >
                    <Play className="w-3 h-3" /> START RESTOCK
                  </button>
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-[color:var(--t-border)]">
                  <div className="font-mono text-[10px] text-[color:var(--t-text-3)]">
                    {pars.length} par-level entr{pars.length === 1 ? 'y' : 'ies'} configured for this room
                  </div>
                  {pars.length === 0 && (
                    <button
                      onClick={seedDefaults}
                      data-testid="restock-seed-btn"
                      className="t-btn-ghost h-7 px-3 text-[10px] font-mono"
                    >
                      SEED DEMO DATA
                    </button>
                  )}
                </div>
              </div>
            )}

            {session && (
              <div className="space-y-4" data-testid="restock-weight-entry">
                <div className="font-mono text-[10px] text-[color:var(--t-text-3)]">
                  Session · <span className="text-[color:var(--t-text-2)]">{session.id}</span> · started {new Date(session.started_at).toLocaleTimeString()}
                </div>
                <div className="grid grid-cols-1 gap-2">
                  {pars.map((p) => {
                    const k = `${p.tray_id}:${p.slot_id}`;
                    const w = weights[k] ?? p.expected_weight_g * p.expected_units;
                    const expected = p.expected_weight_g * p.expected_units;
                    const delta = parseFloat(w) - expected;
                    const deltaColor =
                      Math.abs(delta) < 15 ? 'text-[color:var(--t-text-3)]' :
                      delta < 0 ? 'text-red-400' : 'text-amber-400';
                    return (
                      <div key={k} className="grid grid-cols-12 items-center gap-2 px-3 py-2 rounded border border-[color:var(--t-border)] bg-[color:var(--t-bg)]">
                        <div className="col-span-4 font-mono text-xs">
                          <div className="text-[color:var(--t-text)]">{p.sku}</div>
                          <div className="text-[color:var(--t-text-3)] text-[10px]">slot {p.slot_id} · {p.expected_units}×{p.expected_weight_g}g</div>
                        </div>
                        <div className="col-span-3 font-mono text-[10px] text-[color:var(--t-text-3)]">
                          expected {expected}g
                        </div>
                        <div className="col-span-3">
                          <input
                            type="number"
                            value={w}
                            data-testid={`restock-weight-${p.slot_id}`}
                            onChange={(e) => setWeights({ ...weights, [k]: e.target.value })}
                            className="w-full bg-[color:var(--t-bg-2)] border border-[color:var(--t-border)] rounded px-2 py-1 text-xs font-mono text-[color:var(--t-text)] text-right"
                          />
                        </div>
                        <div className={`col-span-2 font-mono text-[11px] text-right tabular-nums ${deltaColor}`} data-testid={`restock-delta-${p.slot_id}`}>
                          {delta === 0 ? '—' : `${delta > 0 ? '+' : ''}${delta}g`}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={completeRestock}
                    data-testid="restock-complete-btn"
                    className="flex-1 t-btn-primary h-10 px-5 text-xs font-mono inline-flex items-center justify-center gap-1.5"
                  >
                    <Save className="w-3 h-3" /> COMPLETE & RECONCILE
                  </button>
                  <button
                    onClick={() => { setSession(null); setWeights({}); toast.message('Session cancelled'); }}
                    className="t-btn-ghost h-10 px-4 text-xs font-mono"
                    data-testid="restock-cancel-btn"
                  >
                    CANCEL
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Shrinkage log */}
        <section className="lg:col-span-5 space-y-3" data-testid="shrinkage-log-panel">
          <div className="flex items-center justify-between">
            <div className="font-mono text-xs uppercase tracking-[0.18em] text-[color:var(--t-text-3)] flex items-center gap-2">
              <ShieldAlert className="w-3 h-3" /> shrinkage log
            </div>
            <button
              onClick={loadEvents}
              data-testid="shrinkage-refresh"
              className="t-btn-ghost h-7 px-2 text-[10px] font-mono inline-flex items-center gap-1"
            >
              <RefreshCw className={'w-3 h-3 ' + (loadingEvents ? 'animate-spin' : '')} />
            </button>
          </div>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
            {events.length === 0 && !loadingEvents && (
              <div className="border border-dashed border-[color:var(--t-border)] rounded-lg p-6 text-center" data-testid="shrinkage-empty">
                <CheckCircle2 className="w-6 h-6 text-[color:var(--t-emerald)] mx-auto mb-2" />
                <div className="font-mono text-xs text-[color:var(--t-text-2)]">no shrinkage events</div>
                <div className="font-mono text-[10px] text-[color:var(--t-text-3)] mt-1">
                  all inventory accounted for
                </div>
              </div>
            )}
            {events.map((e) => (
              <div
                key={e.id}
                data-testid={`shrinkage-event-${e.id}`}
                className={`border-l-2 rounded-r p-3 bg-[color:var(--t-bg-2)] ${e.resolved
                  ? 'border-[color:var(--t-text-3)] opacity-60'
                  : e.delta_units < 0
                    ? 'border-red-500'
                    : 'border-amber-500'
                  }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-xs text-[color:var(--t-text)]">
                      {e.sku} · slot {e.slot_id}
                    </div>
                    <div className="font-mono text-[10px] text-[color:var(--t-text-3)] mt-0.5">
                      room {e.room_id} · {new Date(e.created_at).toLocaleString()}
                    </div>
                    <div className="font-mono text-[10px] text-[color:var(--t-text-2)] mt-1.5 flex items-center gap-2">
                      <span>expected {e.expected_units_after_restock}</span>
                      <span className="text-[color:var(--t-text-3)]">·</span>
                      <span>actual {e.observed_units_after_restock}</span>
                      <span className={`${e.delta_units < 0 ? 'text-red-400' : 'text-amber-400'} font-semibold`}>
                        {e.delta_units > 0 ? '+' : ''}{e.delta_units}
                      </span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`font-mono font-bold tabular-nums ${e.delta_units < 0 ? 'text-red-400' : 'text-amber-400'}`}>
                      ${Math.abs(e.delta_value_usd).toFixed(2)}
                    </div>
                    <div className="font-mono text-[9px] text-[color:var(--t-text-3)] tracking-wider mt-0.5">
                      {e.reason}
                    </div>
                  </div>
                </div>
                {!e.resolved && (
                  <div className="flex justify-end mt-2 pt-2 border-t border-[color:var(--t-border)]">
                    <button
                      onClick={() => resolveEvent(e.id)}
                      data-testid={`shrinkage-resolve-${e.id}`}
                      className="font-mono text-[10px] px-2 py-1 rounded bg-[color:var(--t-emerald)]/10 text-[color:var(--t-emerald)] hover:bg-[color:var(--t-emerald)]/20 inline-flex items-center gap-1"
                    >
                      <CheckCircle2 className="w-3 h-3" /> MARK RESOLVED
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-[color:var(--t-border)] font-mono text-[10px] text-[color:var(--t-text-3)]">
            <span>{unresolvedCount} unresolved · {events.length - unresolvedCount} cleared</span>
            <span className="inline-flex items-center gap-1">
              <DollarSign className="w-2.5 h-2.5 text-[color:var(--t-amber)]" />
              <AlertTriangle className="w-2.5 h-2.5 text-[color:var(--t-amber)]" />
              layer 4 defense active
            </span>
          </div>
        </section>
        </>
        )}
      </main>

      <TerminalFloorNav />
    </div>
  );
}
